import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface WidgetConfig {
  id: string;
  name: string;
  api_key: string | null;
  api_key_prefix: string;
  allowed_domains: string[];
  allowed_category_ids: string[];
  theme_config: {
    primaryColor?: string;
    borderRadius?: string;
    fontFamily?: string;
  };
  is_active: boolean;
  created_at: string;
}

interface Category {
  id: string;
  name: string;
  slug: string;
  video_count: number;
}

interface CreateWidgetInput {
  name: string;
  allowed_domains?: string[];
  allowed_category_ids?: string[];
}

function buildEmbedCode(apiKey: string): string {
  return `<div id="smc-search-widget"></div>
<script
  src="https://cdn.searchmycontent.com/widget.js"
  data-api-key="${apiKey}"
  data-container="smc-search-widget"
></script>`;
}

function maskKey(key: string): string {
  if (key.length <= 12) return key;
  return `${key.slice(0, 12)}${'•'.repeat(Math.max(0, key.length - 16))}${key.slice(-4)}`;
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          // Clipboard API can fail in some contexts; ignore.
        }
      }}
      className="text-xs font-medium text-blue-600 hover:text-blue-800"
    >
      {copied ? 'Copied!' : label}
    </button>
  );
}

export function WidgetsPage() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingWidget, setEditingWidget] = useState<WidgetConfig | null>(null);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['widgets'],
    queryFn: () => api.get<{ data: WidgetConfig[] }>('/admin/widgets'),
  });

  const { data: categoriesResp } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<{ data: Category[] }>('/categories'),
  });
  const categories = categoriesResp?.data || [];

  const createMutation = useMutation({
    mutationFn: (input: CreateWidgetInput) =>
      api.post<{ data: WidgetConfig & { api_key: string } }>('/admin/widgets', input),
    onSuccess: (response) => {
      setNewApiKey(response.data.api_key);
      queryClient.invalidateQueries({ queryKey: ['widgets'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Partial<CreateWidgetInput> & { is_active?: boolean }) =>
      api.put(`/admin/widgets/${id}`, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['widgets'] });
      setEditingWidget(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/widgets/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['widgets'] }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      api.put(`/admin/widgets/${id}`, { is_active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['widgets'] }),
  });

  const regenerateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/admin/widgets/${id}/regenerate-key`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['widgets'] }),
  });

  const categoryNameById = new Map(categories.map((c) => [c.id, c.name]));

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Widgets</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage embeddable search widgets for your website
          </p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="btn-primary">
          Create Widget
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : data?.data?.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No widgets yet</h3>
          <p className="mt-2 text-sm text-gray-500">
            Create a widget to embed a search interface on your website
          </p>
          <button onClick={() => setShowAddModal(true)} className="btn-primary mt-4">
            Create First Widget
          </button>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          {data?.data?.map((widget) => (
            <WidgetCard
              key={widget.id}
              widget={widget}
              categoryNameById={categoryNameById}
              onEdit={() => setEditingWidget(widget)}
              onToggle={() =>
                toggleMutation.mutate({ id: widget.id, is_active: !widget.is_active })
              }
              onDelete={() => {
                if (confirm('Are you sure you want to delete this widget?')) {
                  deleteMutation.mutate(widget.id);
                }
              }}
              onRegenerate={() => {
                if (
                  confirm(
                    'Regenerate the API key? The old key will stop working immediately.'
                  )
                ) {
                  regenerateMutation.mutate(widget.id);
                }
              }}
              isRegenerating={regenerateMutation.isPending}
            />
          ))}
        </div>
      )}

      {showAddModal && (
        <CreateWidgetModal
          categories={categories}
          onClose={() => {
            setShowAddModal(false);
            setNewApiKey(null);
          }}
          onSubmit={(input) => createMutation.mutate(input)}
          isSubmitting={createMutation.isPending}
          newApiKey={newApiKey}
        />
      )}

      {editingWidget && (
        <EditWidgetModal
          widget={editingWidget}
          categories={categories}
          onClose={() => setEditingWidget(null)}
          onSubmit={(patch) => updateMutation.mutate({ id: editingWidget.id, ...patch })}
          isSubmitting={updateMutation.isPending}
        />
      )}
    </div>
  );
}

function WidgetCard({
  widget,
  categoryNameById,
  onEdit,
  onToggle,
  onDelete,
  onRegenerate,
  isRegenerating,
}: {
  widget: WidgetConfig;
  categoryNameById: Map<string, string>;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
  onRegenerate: () => void;
  isRegenerating: boolean;
}) {
  const [revealed, setRevealed] = useState(false);
  const allowedCategoryNames = (widget.allowed_category_ids || [])
    .map((id) => categoryNameById.get(id))
    .filter((name): name is string => Boolean(name));
  const hasKey = Boolean(widget.api_key);
  const displayKey = widget.api_key
    ? revealed
      ? widget.api_key
      : maskKey(widget.api_key)
    : `${widget.api_key_prefix}… (key not stored — regenerate to retrieve)`;
  const embedCode = buildEmbedCode(widget.api_key || 'YOUR_API_KEY_HERE');

  return (
    <div className="card">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">{widget.name}</h3>
          <span
            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
              widget.is_active
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            {widget.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      <div className="p-6 space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">API Key</h4>
            <div className="flex gap-3">
              {hasKey && (
                <button
                  type="button"
                  onClick={() => setRevealed((v) => !v)}
                  className="text-xs font-medium text-gray-600 hover:text-gray-900"
                >
                  {revealed ? 'Hide' : 'Show'}
                </button>
              )}
              {hasKey && widget.api_key && <CopyButton text={widget.api_key} />}
              <button
                type="button"
                onClick={onRegenerate}
                disabled={isRegenerating}
                className="text-xs font-medium text-amber-600 hover:text-amber-800 disabled:opacity-50"
              >
                {isRegenerating ? 'Regenerating…' : 'Regenerate'}
              </button>
            </div>
          </div>
          <p className="font-mono text-sm break-all select-all bg-gray-50 p-3 rounded-lg">
            {displayKey}
          </p>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Allowed Domains</h4>
          {widget.allowed_domains.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {widget.allowed_domains.map((domain, i) => (
                <span
                  key={i}
                  className="inline-flex px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded"
                >
                  {domain}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">All domains allowed</p>
          )}
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Categories</h4>
          {allowedCategoryNames.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {allowedCategoryNames.map((name, i) => (
                <span
                  key={i}
                  className="inline-flex px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded"
                >
                  {name}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">All categories</p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">Embed Code</h4>
            <CopyButton text={embedCode} />
          </div>
          <pre className="text-xs bg-gray-50 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap break-all">
            {embedCode}
          </pre>
        </div>
      </div>

      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-3">
        <button
          onClick={onEdit}
          className="text-sm font-medium text-blue-600 hover:text-blue-900"
        >
          Edit
        </button>
        <button
          onClick={onToggle}
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          {widget.is_active ? 'Disable' : 'Enable'}
        </button>
        <button
          onClick={onDelete}
          className="text-sm font-medium text-red-600 hover:text-red-900"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function CategoryMultiSelect({
  categories,
  value,
  onChange,
}: {
  categories: Category[];
  value: string[];
  onChange: (value: string[]) => void;
}) {
  if (categories.length === 0) {
    return (
      <p className="text-xs text-gray-500">
        No categories defined yet. Create some on the Categories page first.
      </p>
    );
  }
  return (
    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
      {categories.map((cat) => {
        const checked = value.includes(cat.id);
        return (
          <label
            key={cat.id}
            className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded"
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => {
                if (e.target.checked) {
                  onChange([...value, cat.id]);
                } else {
                  onChange(value.filter((id) => id !== cat.id));
                }
              }}
              className="rounded border-gray-300"
            />
            <span>{cat.name}</span>
            <span className="text-xs text-gray-400">({cat.video_count})</span>
          </label>
        );
      })}
    </div>
  );
}

function CreateWidgetModal({
  categories,
  onClose,
  onSubmit,
  isSubmitting,
  newApiKey,
}: {
  categories: Category[];
  onClose: () => void;
  onSubmit: (input: CreateWidgetInput) => void;
  isSubmitting: boolean;
  newApiKey: string | null;
}) {
  const [name, setName] = useState('');
  const [domains, setDomains] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const allowedDomains = domains
      .split('\n')
      .map((d) => d.trim())
      .filter((d) => d);
    onSubmit({
      name,
      allowed_domains: allowedDomains.length > 0 ? allowedDomains : undefined,
      allowed_category_ids: selectedCategories.length > 0 ? selectedCategories : undefined,
    });
  };

  if (newApiKey) {
    const embedCode = buildEmbedCode(newApiKey);
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-gray-600/75" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
              </svg>
            </div>
            <h2 className="mt-4 text-lg font-semibold text-gray-900">Widget Created!</h2>
            <p className="mt-2 text-sm text-gray-500">
              Save this API key now. It won't be shown again.
            </p>
          </div>

          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs text-gray-500">API Key</p>
              <CopyButton text={newApiKey} />
            </div>
            <p className="font-mono text-sm break-all select-all">{newApiKey}</p>
          </div>

          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs text-gray-500">Embed Code</p>
              <CopyButton text={embedCode} />
            </div>
            <pre className="text-xs font-mono whitespace-pre-wrap break-all select-all">
              {embedCode}
            </pre>
          </div>

          <button onClick={onClose} className="btn-primary w-full mt-6">
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-gray-600/75" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Create Widget</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder="e.g., Main Website Widget"
              required
            />
          </div>

          <div>
            <label className="label">Allowed Domains (one per line, optional)</label>
            <textarea
              value={domains}
              onChange={(e) => setDomains(e.target.value)}
              className="input"
              rows={3}
              placeholder="example.com&#10;*.example.com"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to allow all domains. Use * for wildcard subdomains.
            </p>
          </div>

          <div>
            <label className="label">Restrict to Categories (optional)</label>
            <CategoryMultiSelect
              categories={categories}
              value={selectedCategories}
              onChange={setSelectedCategories}
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to search across all categories.
            </p>
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? (
                <>
                  <LoadingSpinner size="sm" />
                  Creating...
                </>
              ) : (
                'Create Widget'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditWidgetModal({
  widget,
  categories,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  widget: WidgetConfig;
  categories: Category[];
  onClose: () => void;
  onSubmit: (patch: Partial<CreateWidgetInput>) => void;
  isSubmitting: boolean;
}) {
  const [name, setName] = useState(widget.name);
  const [domains, setDomains] = useState((widget.allowed_domains || []).join('\n'));
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    widget.allowed_category_ids || []
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const allowedDomains = domains
      .split('\n')
      .map((d) => d.trim())
      .filter((d) => d);
    onSubmit({
      name,
      allowed_domains: allowedDomains,
      allowed_category_ids: selectedCategories,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-gray-600/75" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Edit Widget</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Allowed Domains (one per line)</label>
            <textarea
              value={domains}
              onChange={(e) => setDomains(e.target.value)}
              className="input"
              rows={3}
              placeholder="example.com&#10;*.example.com"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to allow all domains.
            </p>
          </div>

          <div>
            <label className="label">Restrict to Categories</label>
            <CategoryMultiSelect
              categories={categories}
              value={selectedCategories}
              onChange={setSelectedCategories}
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to search across all categories.
            </p>
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? (
                <>
                  <LoadingSpinner size="sm" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
